import { USState } from "@/src/lib/us-states";

interface GeocodingResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface SearchPlacesOptions {
  biasStateCode?: USState;
  restrictStateCode?: USState;
}

export type AddressGeocodeSuggestion = {
  placeId: string;
  label: string;
};

export type AddressGeocodingClient = {
  autocompletePlaces: (params: AutocompletePlacesParams) => Promise<AddressGeocodeSuggestion[]>;
  fetchPlaceDetails: (placeId: string) => Promise<Record<string, unknown>>;
};

const STAFF_GEOCODING_ENDPOINT = "/api/geocode";
const ONLINE_SCHEDULING_GEOCODING_ENDPOINT = "/api/online-scheduling/geocode";

async function postGeocoding<T>(endpoint: string, body: Record<string, unknown>, fallbackError: string): Promise<T> {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const result = (await response.json()) as GeocodingResponse<T>;

  if (!result.success || result.data === undefined) {
    throw new Error(result.error || fallbackError);
  }

  return result.data;
}

export async function fetchPlaceDetails(
  placeId: string,
  endpoint = STAFF_GEOCODING_ENDPOINT
): Promise<Record<string, unknown>> {
  return postGeocoding(endpoint, { action: "placeDetails", data: { placeId } }, "Failed to fetch place details");
}

export async function searchPlacesFromText(
  query: string,
  maxResults: number = 5,
  options?: SearchPlacesOptions
): Promise<Record<string, unknown>[]> {
  return postGeocoding(
    STAFF_GEOCODING_ENDPOINT,
    {
      action: "searchPlaces",
      data: {
        query,
        maxResults,
        biasStateCode: options?.biasStateCode,
        restrictStateCode: options?.restrictStateCode,
      },
    },
    "Failed to search places"
  );
}

export interface AutocompletePlacesParams {
  input: string;
  maxResults?: number;
  stateCode?: USState;
}

export async function autocompletePlaces(params: AutocompletePlacesParams): Promise<Record<string, unknown>[]> {
  const { input, maxResults = 5, stateCode } = params;

  return postGeocoding(
    STAFF_GEOCODING_ENDPOINT,
    {
      action: "autocompletePlaces",
      data: { input, maxResults, stateCode },
    },
    "Failed to autocomplete places"
  );
}

export const onlineSchedulingGeocodingClient: AddressGeocodingClient = {
  autocompletePlaces: (params) =>
    postGeocoding(
      ONLINE_SCHEDULING_GEOCODING_ENDPOINT,
      { action: "autocompletePlaces", data: params },
      "Failed to search addresses"
    ),
  fetchPlaceDetails: (placeId) => fetchPlaceDetails(placeId, ONLINE_SCHEDULING_GEOCODING_ENDPOINT),
};
